import * as React from 'react';
import { useEffect } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, ChevronDown, WandSparkles, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Plus from '@/assets/svgs/circle-plus.svg?react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  'm-1 px-1.5 py-1 text-xs font-normal transition delay-150 duration-300 ease-in-out',
  {
    variants: {
      variant: {
        default: 'border bg-input text-foreground hover:bg-primary/20',
        secondary:
          'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: string;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;
    /**
     * Optional avatar image URL. When defined (even if null/undefined),
     * a small avatar circle is rendered in the chip. Pass null to show initials only.
     */
    avatarLink?: string | null;
  }[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * If true, disables the multi-select component.
   * Optional, defaults to false.
   */
  disabled?: boolean;

  /**
   * If true, shows the Select All option. Optional, defaults to true.
   */
  isSelectAllButtonVisible?: boolean;

  /**
   * If true, allows creating new options when search input doesn't match existing options.
   * Optional, defaults to false.
   */
  isCreatable?: boolean;

  /**
   * Callback function triggered when a new option is created.
   * Receives the input value and should return the new option or a promise that resolves to the new option.
   */
  onCreateOption?: (
    inputValue: string
  ) =>
    | { label: string; value: string; icon?: React.ComponentType<{ className?: string }> }
    | Promise<{ label: string; value: string; icon?: React.ComponentType<{ className?: string }> }>;

  /**
   * Callback triggered when the popover open state changes.
   * Useful for lazy-loading options on first open.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * If true, renders an Apply button in the footer that closes the dropdown.
   * Optional, defaults to false.
   */
  isApplyButtonVisible?: boolean;

  /**
   * If true, renders a compact selected-count badge in the trigger.
   * Optional, defaults to false.
   */
  isSelectedCountVisible?: boolean;

  /**
   * Called whenever the search input value changes. Use to trigger server-side filtering.
   */
  onSearchChange?: (search: string) => void;

  /**
   * Overrides the search input placeholder. Defaults to the shared "Search..." copy.
   */
  searchPlaceholder?: string;

  /**
   * Overrides the message shown when no options match the search.
   * Defaults to the shared "No results found" copy.
   */
  emptyMessage?: string;

  /**
   * When true, hides the bottom footer (Clear all / "Selected N items") while no options
   * are selected. Useful for tag/option editors where an empty "Selected 0 items" is noise.
   * Optional, defaults to false.
   */
  hideFooterWhenEmpty?: boolean;

  /**
   * Controls how each option row in the dropdown list is rendered:
   * - `'checkbox'` (default): a checkbox toggles selection on row click.
   * - `'removable'`: the row shows a trailing "×" that removes the option; the rest of the
   *   row is inert. Designed for option-editor use cases where every listed value is also a
   *   defined option, so the only meaningful action is deletion.
   * Optional, defaults to `'checkbox'`.
   */
  optionItemVariant?: 'checkbox' | 'removable';
}

const TAG = 'Multiselect:';
const CUSTOM_PREFIX = 'custom:';

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder,
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      disabled = false,
      isSelectAllButtonVisible = true,
      isCreatable = false,
      onCreateOption,
      onOpenChange,
      isApplyButtonVisible = false,
      isSelectedCountVisible = false,
      onSearchChange,
      searchPlaceholder,
      emptyMessage,
      hideFooterWhenEmpty = false,
      optionItemVariant = 'checkbox',
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpenInternal] = React.useState(false);
    const setIsPopoverOpen = React.useCallback(
      (openOrUpdater: boolean | ((prev: boolean) => boolean)) => {
        setIsPopoverOpenInternal(prev => {
          const next = typeof openOrUpdater === 'function' ? openOrUpdater(prev) : openOrUpdater;
          if (next !== prev) {
            onOpenChange?.(next);
          }
          return next;
        });
      },
      [onOpenChange]
    );
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');

    const { t } = useTranslation('common');

    useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);

    // Ensure selectedValues only contains valid options (including custom options)
    React.useEffect(() => {
      // Don't filter if options haven't loaded yet (empty options array)
      if (options.length === 0) return;

      // When isCreatable is true, keep all selected values — they may be user-created
      // entries that aren't in the official options list
      if (isCreatable) return;

      const validOptionValues = options.map(opt => opt.value);
      const validSelectedValues = selectedValues.filter(value => validOptionValues.includes(value));

      // Only update if there's a difference to avoid infinite loops
      if (validSelectedValues.length !== selectedValues.length) {
        setSelectedValues(validSelectedValues);
        onValueChange(validSelectedValues);
      }
    }, [options, selectedValues, onValueChange, isCreatable]);

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter(value => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      if (!disabled) {
        setIsPopoverOpen(prev => !prev);
      }
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleApply = () => {
      setIsPopoverOpen(false);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map(option => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    return (
      <Popover
        open={disabled ? false : isPopoverOpen}
        onOpenChange={disabled ? undefined : setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            disabled={disabled}
            onClick={handleTogglePopover}
            className={cn(
              'flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto',
              'disabled:border-input disabled:opacity-100 disabled:bg-muted disabled:pointer-events-none',
              className
            )}
          >
            {selectedValues.length > 0 ? (
              <div className="flex w-full min-w-0 items-center justify-between">
                <div className="flex min-w-0 flex-wrap items-center">
                  {selectedValues.slice(0, maxCount).map(value => {
                    const option = options.find(o => o.value === value);
                    const IconComponent = option?.icon;

                    // Handle custom options that start with "custom:"
                    const displayLabel =
                      option?.label ||
                      (value.startsWith(CUSTOM_PREFIX) ? value.replace(CUSTOM_PREFIX, '') : value);

                    const hasAvatar = option && 'avatarLink' in option;

                    return (
                      <Badge
                        key={value}
                        className={cn(
                          'max-w-full',
                          isAnimating ? 'animate-bounce' : '',
                          multiSelectVariants({ variant })
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {hasAvatar && (
                          <span className="mr-1.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-[8px] font-semibold uppercase">
                            {option!.avatarLink ? (
                              <img
                                src={option!.avatarLink}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              displayLabel.charAt(0)
                            )}
                          </span>
                        )}
                        {IconComponent && <IconComponent className="mr-2 h-4 w-4 flex-shrink-0" />}
                        <span className="truncate">{displayLabel}</span>
                        {!disabled && (
                          <XIcon
                            className="ml-0.5 h-4 w-4 cursor-pointer"
                            onClick={event => {
                              event.stopPropagation();
                              toggleOption(value);
                            }}
                          />
                        )}
                      </Badge>
                    );
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        'bg-transparent text-foreground border-foreground/1',
                        isAnimating ? 'animate-bounce' : '',
                        multiSelectVariants({ variant })
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {t('moreSelected', { count: selectedValues.length - maxCount })}
                      {!disabled && (
                        <XIcon
                          className="ml-0.5 h-4 w-4 cursor-pointer"
                          onClick={event => {
                            event.stopPropagation();
                            clearExtraOptions();
                          }}
                        />
                      )}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2 pl-2">
                  {isSelectedCountVisible && (
                    <Badge
                      variant="secondary"
                      className="m-0 h-6 max-w-[7.5rem] shrink-0 rounded-full px-2 text-xs font-medium"
                    >
                      <span className="truncate">
                        {t('table.selected', { count: selectedValues.length })}
                      </span>
                    </Badge>
                  )}
                  <ChevronDown
                    className={cn('h-4 mx-2 text-muted-foreground', disabled && 'cursor-default')}
                  />
                </div>
              </div>
            ) : (
              <div className="mx-auto flex w-full items-center justify-between">
                <span className="mx-3 text-sm font-normal text-muted-foreground">
                  {placeholder || t('selectOptions')}
                </span>
                <ChevronDown
                  className={cn('h-4 text-muted-foreground mx-2', disabled && 'cursor-default')}
                />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="flex max-h-80 w-auto min-w-80 flex-col p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command className="flex flex-1 flex-col" shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder ?? t('search')}
              onKeyDown={handleInputKeyDown}
              value={searchValue}
              onValueChange={value => {
                setSearchValue(value);
                onSearchChange?.(value);
              }}
            />
            <CommandList className="flex-1 overflow-y-auto">
              <CommandEmpty>{emptyMessage ?? t('noResultsFound')}</CommandEmpty>
              <CommandGroup>
                {isSelectAllButtonVisible && (
                  <CommandItem key="all" onSelect={toggleAll} className="cursor-pointer">
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        selectedValues.length === options.length
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>{`(${t('selectAll')})`}</span>
                  </CommandItem>
                )}
                {(() => {
                  // Combine original options with user-created options that are currently selected
                  // These are values not present in the official options list
                  const optionValues = new Set(options.map(opt => opt.value));
                  const customOptions = selectedValues
                    .filter(value => !optionValues.has(value))
                    .map(value => ({
                      label: value.startsWith(CUSTOM_PREFIX)
                        ? value.replace(CUSTOM_PREFIX, '')
                        : value,
                      value: value,
                      icon: undefined,
                    }));

                  const allOptions = [...options, ...customOptions];

                  // Filter options based on search value
                  const filteredOptions = searchValue
                    ? allOptions.filter(option =>
                        option.label.toLowerCase().includes(searchValue.toLowerCase())
                      )
                    : allOptions;

                  return filteredOptions.map(option => {
                    const isSelected = selectedValues.includes(option.value);
                    const isRemovable = optionItemVariant === 'removable';

                    return (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        // In removable mode the row itself is inert — only the trailing
                        // "×" mutates the list — so selecting the row is a no-op.
                        onSelect={() => {
                          if (!isRemovable) toggleOption(option.value);
                        }}
                        className={cn(
                          isRemovable ? 'flex items-center justify-between' : 'cursor-pointer'
                        )}
                      >
                        <div className="flex min-w-0 items-center">
                          {!isRemovable && (
                            <div
                              className={cn(
                                'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                isSelected
                                  ? 'bg-primary text-primary-foreground'
                                  : 'opacity-50 [&_svg]:invisible'
                              )}
                            >
                              <CheckIcon className="h-4 w-4" />
                            </div>
                          )}
                          {'avatarLink' in option && (
                            <span className="mr-2 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-[9px] font-semibold uppercase">
                              {option.avatarLink ? (
                                <img
                                  src={option.avatarLink}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                option.label.charAt(0)
                              )}
                            </span>
                          )}
                          {option.icon && (
                            <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="truncate">{option.label}</span>
                        </div>
                        {isRemovable && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-2 h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground"
                            onClick={event => {
                              event.stopPropagation();
                              toggleOption(option.value);
                            }}
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </CommandItem>
                    );
                  });
                })()}
              </CommandGroup>
              {isCreatable &&
                searchValue &&
                !options.some(
                  option => option.label.toLowerCase() === searchValue.toLowerCase()
                ) && (
                  <CommandGroup className="pt-0">
                    <CommandItem
                      onSelect={async () => {
                        if (onCreateOption) {
                          try {
                            const newOption = await onCreateOption(searchValue);
                            toggleOption(newOption.value);
                            setSearchValue('');
                          } catch (error) {
                            logger.error(TAG, 'Failed to create option:', error);
                          }
                        }
                      }}
                      className="cursor-pointer"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      <span>{`${t('add')} "${searchValue}"`}</span>
                    </CommandItem>
                  </CommandGroup>
                )}
            </CommandList>
          </Command>

          {!(hideFooterWhenEmpty && selectedValues.length === 0) && (
            <div className="border-t bg-background p-3">
              <div className="flex items-center justify-between gap-4">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 flex-shrink-0"
                  onClick={handleClear}
                  disabled={selectedValues.length === 0}
                >
                  {t('filters.clearAll')}
                </Button>
                <div className="flex items-center gap-3">
                  <p className="flex-shrink-0 text-sm text-muted-foreground">
                    {t('filters.selected', { count: selectedValues.length })}
                  </p>
                  {isApplyButtonVisible && (
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 flex-shrink-0"
                      onClick={handleApply}
                    >
                      {t('filters.apply')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              'cursor-pointer my-2 text-foreground bg-background w-3 h-3',
              isAnimating ? '' : 'text-muted-foreground'
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';
